import { Injectable } from "@angular/core";
import { EMPTY, Observable, Subject } from "rxjs";
import { GenericService, LoaderConfig, ErrorConfig } from "./generic.service";
import { environment } from "src/environments/environment";
import { ServerResponse } from "../models/ServerResponse";
import { DialogRef, FilterOperator, ISAPI, ISDialogService, ISPagination, MVFilter, MVListingConfigs } from "is-lib";
// Aliased: models/Contract also exports an IContract (the contracts list item)
import { IContract as IContractForm } from "../models/ContractResponse";
import { UsersService } from "./users.service";
import { ca, tr } from "intl-tel-input/i18n";
import { HttpParams } from "@angular/common/http";
import { ContractAccessRequest } from "../models/ContractAccessRequest";
import { IContract, IContractDetails } from "../models/Contract";
import { IContractElectricitySupply } from "../models/ElectricitySupply";
import { IContractNaturalGasSupply } from "../models/NaturalGasSupply";
import { IContractSelfConsumptionSupply } from "../models/SelfConsumptionSupply";
import { ElectricitySupplyContractEconomicCondition, ElectricitySupplyContracts } from "../models/ElectricitySupplyContracts";
import { ICreateContractElectricitySupplyProposal, ICreateContractEnergySupplyProposal } from "../models/EnergySupplyProposals";

@Injectable({
    providedIn: `root`
})

export class ContractsService{

    constructor(private _httpService: GenericService, private _user: UsersService, private _isDialog: ISDialogService) { }

    getContractAccessRequests(body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/ContractAccessRequests/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    getContract(contractId: string, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean){
        return this._httpService.get<IContractForm>(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}`, loaderConfig, undefined, undefined, errorConfig);
    }

    getContractFiles(contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/Files/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    getContractHistory(contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/History/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    getContracts(body: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<IContract[]>> {
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    getContractElectricitySupplies(contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<IContractElectricitySupply[]>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/ElectricitySupplies/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    getContractElectricitySuppliesId(electricitySupplyId: string, contractId: string, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>> {
        return this._httpService.get(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/ElectricitySupplies/${electricitySupplyId}`, loaderConfig, undefined, undefined, errorConfig);
    }

    getContractElectricitySuppliesHistory(electricitySupplyId: string, contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/ElectricitySupplies/${electricitySupplyId}/History/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    getContractNaturalGasSupplies(contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<IContractNaturalGasSupply[]>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/NaturalGasSupplies/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    getContractNaturalGasSuppliesId(gasSupplyId: string, contractId: string, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>> {
        return this._httpService.get(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/NaturalGasSupplies/${gasSupplyId}`, loaderConfig, undefined, undefined, errorConfig);
    }

    getContractNaturalGasSuppliesHistory(naturalGasSupplyId: string, contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/NaturalGasSupplies/${naturalGasSupplyId}/History/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    getContractSelfConsumptionSupplies(contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<IContractSelfConsumptionSupply[]>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/SelfConsumptionContracts/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }
    
    getContractSelfConsumptionSuppliesId(selfConsumptionSupplyId: string, contractId: string, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>> {
        return this._httpService.get(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/SelfConsumptionContracts/${selfConsumptionSupplyId}`, loaderConfig, undefined, undefined, errorConfig);
    }

    getContractSelfConsumptionSuppliesHistory(selfConsumptionSupplyId: string, contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>> {
        if (!body) {
            body = new MVListingConfigs([], null, new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/${contractId}/SelfConsumptionContracts/${selfConsumptionSupplyId}/History/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    getContractDetails(contractGuid: string, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<IContractDetails>> {
        return this._httpService.get<IContractDetails>(`${environment.apiUrl}Contracts/${this._user.userCountryCode}/${contractGuid}`, loaderConfig, undefined, undefined, errorConfig);
    }

    getContractsStatuses(loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>>{
        return this._httpService.get(`${environment.apiUrl}Contracts/${this._user.userCountryCode}/Statuses`, loaderConfig, undefined, undefined, errorConfig);
    }

    getContractTypesByEntityTypeDescription(entityType: string, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean) {
        let params = new HttpParams().set('entityType', entityType);

        return this._httpService.get(`${environment.apiUrl}Contracts/${this._user.userCountryCode}/TypesByEntityType`, loaderConfig, params, undefined, errorConfig)
    }

    getContractContactPeople(contractId: string, body?: ISAPI, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>> {
        if (!body) {
            body = new ISAPI([], new ISPagination(999, 0));
        }
        return this._httpService.post(`${environment.apiUrl}Contracts/${this._user.userData?.countryCode}/${contractId}/ContactPeople/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    getContractHistoryComparison(contractId: string, historyId: string, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>>{
        return this._httpService.get(`${environment.apiUrl}Contracts/${this._user.userCountryCode}/${contractId}/History/Status/${historyId}/Comparison`, loaderConfig, undefined, undefined, errorConfig);
    }

    getContractAccessRequest(body: ContractAccessRequest, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>> {
        // if (!body) {
        //     body = new ISAPI([], new ISPagination(999, 0));
        // }
        return this._httpService.post(`${environment.apiUrl}ContractAccesses/${this._user.userCountryCode}/Mine/ContractAccessRequest`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    getElectricitySupplyContractsOfContract(contractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<ElectricitySupplyContracts[]>> {
        if (String.isNullOrUndefinedOrWhitespace(contractId)) {
            return EMPTY;
        }
        
        if (!body) {
            body = new MVListingConfigs([],
            null,
            new ISPagination(999, 0));
        }
        
        body.filters.push(new MVFilter('FkMVContract', contractId, 'id', FilterOperator.equals));

        return this._httpService.post(`${environment.apiUrl}ElectricitySupplyContracts/${this._user.userCountryCode}/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    getElectricitySupplyContractEconomicConditions(elecSupplyContractId: string, body?: MVListingConfigs, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<ElectricitySupplyContractEconomicCondition[]>> {
        if (String.isNullOrUndefinedOrWhitespace(elecSupplyContractId)) {
            return EMPTY;
        }
        
        if (!body) {
            body = new MVListingConfigs([],
            null,
            new ISPagination(999, 0));
        }

        return this._httpService.post(`${environment.apiUrl}ElectricitySupplyContracts/${this._user.userCountryCode}/${elecSupplyContractId}/EconomicConditions/Get`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    createContractEnergySupplyProposal(contractId: string, body: ICreateContractEnergySupplyProposal, loaderConfig: LoaderConfig | boolean = true, errorConfig?: ErrorConfig | boolean): Observable<ServerResponse<any>> {
        return this._httpService.post(`${environment.apiUrl}Contracts/${this._user.userCountryCode}/${contractId}/EnergySupplyProposal`, body, undefined, loaderConfig, undefined, errorConfig);
    }

    associateElectricitySupplyToContractEnergySupplyProposal(body: ICreateContractElectricitySupplyProposal, contractEnergySupplyProposalId: string, loaderConfig: LoaderConfig | boolean = true, errorConfig?: ErrorConfig | boolean): Observable<any> {
        return this._httpService.post(`${environment.apiUrl}ContractEnergySupplyProposals/${this._user.userCountryCode}/${contractEnergySupplyProposalId}/ElectricitySupplyProposal`, body, undefined, loaderConfig, undefined, errorConfig);
    }
}